import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageTitle } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Save, RefreshCw, ShieldCheck, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { invalidateFeatureAccessCache } from "@/hooks/useFeatureAccess";

const FEATURES = [
  { id: "tabungan", label: "Tabungan" },
  { id: "budget", label: "Budget" },
  { id: "seserahan", label: "List Seserahan" },
  { id: "rundown", label: "Rundown Acara" },
  { id: "vendor", label: "Kontak Vendor" },
  { id: "perbandingan", label: "Perbandingan Vendor" },
  { id: "tamu", label: "List Tamu" },
  { id: "life", label: "Life After Married" },
  { id: "todo", label: "To Do List" },
  { id: "dokumen", label: "List Dokumen" },
  { id: "pembayaran", label: "Pembayaran" },
  { id: "undangan", label: "Kustomisasi Undangan" },
  { id: "moodboard", label: "Moodboard" },
  { id: "skenario_budget", label: "Skenario Budget" },
  { id: "vendor_pilihan", label: "Vendor Pilihan" },
  { id: "template_standard", label: "Undangan Template Standard" },
  { id: "template_premium", label: "Undangan Template Premium" },
];

const PLANS = [
  { id: "bulan1", label: "1 Bulan" },
  { id: "bulan6", label: "6 Bulan" },
  { id: "premium", label: "Premium" },
];

const QUOTAS = [
  { id: "max_tamu", label: "Jumlah Maksimal Tamu Undangan" },
  { id: "max_wa_templates", label: "Maksimal Template Blast WhatsApp" },
  { id: "max_gallery_photos", label: "Maksimal Foto Galeri Undangan" },
];

export default function FeatureAccessAdmin() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feature-access");
      setConfig(data);
    } catch {
      toast.error("Gagal memuat pengaturan");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (fid, pid) => {
    setConfig((c) => ({
      ...c,
      features: {
        ...c.features,
        [fid]: { ...c.features[fid], [pid]: !c.features[fid]?.[pid] },
      },
    }));
  };

  const setQuota = (qid, pid, value) => {
    setConfig((c) => ({
      ...c,
      quotas: { ...c.quotas, [qid]: { ...c.quotas[qid], [pid]: value } },
    }));
  };

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await api.put("/admin/feature-access", {
        features: config.features,
        quotas: config.quotas,
      });
      invalidateFeatureAccessCache();
      toast.success("Pengaturan disimpan dan berlaku langsung");
    } catch {
      toast.error("Gagal menyimpan");
    } finally { setSaving(false); }
  };

  if (loading || !config) {
    return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="feature-access-loading">Memuat…</div>;
  }

  return (
    <div className="space-y-6" data-testid="feature-access-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageTitle icon={ShieldCheck} title="Manajemen Akses Fitur"
          subtitle="Atur fitur mana yang dapat diakses oleh setiap paket langganan dan batas kuantitasnya. Sysadmin & Admin selalu bypass semua batasan." />
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" data-testid="fa-reload">
            <RefreshCw className="mr-1.5 h-4 w-4" /> Muat Ulang
          </Button>
          <Button onClick={save} disabled={saving} data-testid="fa-save-top">
            <Save className="mr-1.5 h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan"}
          </Button>
        </div>
      </div>

      {/* FEATURE TOGGLE MATRIX */}
      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="sticky left-0 z-10 bg-muted/50 px-4 py-3 text-left font-semibold">Fitur</th>
              {PLANS.map((p) => (
                <th key={p.id} className="px-4 py-3 text-center font-semibold" data-testid={`fa-header-${p.id}`}>
                  Paket {p.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURES.map((f) => (
              <tr key={f.id} className="border-t border-border" data-testid={`fa-row-${f.id}`}>
                <td className="sticky left-0 z-10 bg-background px-4 py-3">{f.label}</td>
                {PLANS.map((p) => {
                  const on = !!config.features?.[f.id]?.[p.id];
                  return (
                    <td key={p.id} className="px-4 py-3 text-center">
                      <Switch
                        checked={on}
                        onCheckedChange={() => toggle(f.id, p.id)}
                        data-testid={`fa-toggle-${f.id}-${p.id}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* QUOTA MATRIX */}
      <Card className="space-y-5 p-6" data-testid="fa-quotas">
        <div>
          <p className="text-lg font-semibold">Batas Kuantitas per Paket</p>
          <p className="text-xs text-muted-foreground">
            Kosongkan / aktifkan <b>Tidak Terbatas</b> untuk mengizinkan tanpa batas.
          </p>
        </div>
        {QUOTAS.map((q) => (
          <div key={q.id} className="grid gap-3 rounded-lg border border-border p-4 md:grid-cols-4" data-testid={`fa-quota-${q.id}`}>
            <div className="md:col-span-1">
              <p className="text-sm font-semibold">{q.label}</p>
            </div>
            {PLANS.map((p) => {
              const raw = config.quotas?.[q.id]?.[p.id];
              const isUnlimited = raw === null || raw === undefined;
              return (
                <div key={p.id} className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Paket {p.label}</p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number" min={0}
                      disabled={isUnlimited}
                      value={isUnlimited ? "" : (raw ?? 0)}
                      onChange={(e) => {
                        const v = e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0);
                        setQuota(q.id, p.id, v);
                      }}
                      placeholder={isUnlimited ? "∞" : "0"}
                      data-testid={`fa-quota-input-${q.id}-${p.id}`}
                    />
                    <label className="flex items-center gap-1.5 whitespace-nowrap text-xs">
                      <Switch
                        checked={isUnlimited}
                        onCheckedChange={(v) => setQuota(q.id, p.id, v ? null : 0)}
                        data-testid={`fa-quota-unlim-${q.id}-${p.id}`}
                      />
                      <InfinityIcon className="h-3.5 w-3.5" />
                      <span>Tidak Terbatas</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="fa-save-bottom">
          <Save className="mr-1.5 h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan Perubahan"}
        </Button>
      </div>
    </div>
  );
}
